import React from 'react';
import { buildClassString } from '../StringUtils';
import './style/index.scss';

export interface SharedSlideoutProps {
    /** How long to wait before animating the slideout open (default 100) */
    animationDelayMs?: number;
    /** Class name added to the slideout root */
    className?: string;
    /** Class name added to the slideout panel */
    panelClassName?: string;
    /** Show the close button in the upper-right hand corner? */
    useCloseButton?: boolean;
    /** Content to render in the slideout */
    children: React.ReactNode;
    /** Toggle slideout open/closed state */
    isOpen: boolean;
    /** Callback when slideout is closed */
    handleCloseSlideout(): void;
    /** Callback after slideout finishes closing */
    onCloseFinish?(): void;
    /** Callbak after slideout finishes opening */
    onOpenFinish?(): void;
}

export interface SharedSlideoutState {
    readyToAnimate: boolean;
    isMounted: boolean;
}

const DEFAULT_ANIMATION_DELAY_MS = 100;
const ON_OPEN_CLOSE_FINISH_DELAY_MS = 100;

export class SharedSlideout extends React.Component<SharedSlideoutProps, SharedSlideoutState> {

    private slideoutRef: HTMLDivElement;

    constructor(props: SharedSlideoutProps) {
        super(props);
        this.closeSlideout = this.closeSlideout.bind(this);
        this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
        this.state = {
            readyToAnimate: false,
            isMounted: false
        };
    }

    componentDidMount(): void {
        this.setState({isMounted: true});
        if (this.props.isOpen) {
            this.triggerAnimation();
        }

        if (this.slideoutRef) {
            this.slideoutRef.addEventListener('transitionend', this.handleTransitionEnd);
        }
    }

    componentDidUpdate(nextProps: SharedSlideoutProps): void {
        if (this.props.isOpen !== nextProps.isOpen) {
            this.triggerAnimation();
        }
    }

    triggerAnimation(): void {
        const { animationDelayMs } = this.props;
        setTimeout(() => {
            this.setState({
                readyToAnimate: !this.state.readyToAnimate
            });
        }, animationDelayMs || DEFAULT_ANIMATION_DELAY_MS);
    }

    handleTransitionEnd(evt: TransitionEvent): void {
        // Since the slideout transition CSS is transitioning all, the transitionend
        // event is fired for each property that changed. The slideout animates by using
        // transform: translate..., so this conditional makes it so we only invoke the
        // open/close callbacks one time after the slideout truly finishes opening or closing
        if (evt.propertyName !== 'transform') {
            return;
        }

        // Transform transitions in children will propagate here
        // But we only want to invoke callbacks for this slideout's transition
        if (evt.target !== evt.currentTarget) {
            return;
        }

        const { isOpen, onCloseFinish, onOpenFinish } = this.props;
        if (isOpen && onOpenFinish) {
            setTimeout(onOpenFinish, ON_OPEN_CLOSE_FINISH_DELAY_MS);
        } else if (!isOpen && onCloseFinish) {
            setTimeout(onCloseFinish, ON_OPEN_CLOSE_FINISH_DELAY_MS);
        }
    }

    render(): React.ReactNode {
        const {
            children,
            className,
            isOpen,
            panelClassName,
            useCloseButton
        } = this.props;

        const {
            readyToAnimate,
            isMounted
        } = this.state;

        const slideoutClassName = buildClassString({
            [className as string]: !!className,
            'shared-slideout': true,
            'shared-slideout--toggled': isOpen && readyToAnimate && isMounted
        });

        return (
            <div className={slideoutClassName} ref={(ref: any) => this.slideoutRef = ref}>
                <div className="shared-slideout__overlay" onClick={this.closeSlideout} />
                <div className={`shared-slideout__panel ${panelClassName ?? ''}`} >
                    {useCloseButton &&
                        <div className="shared-slideout__close-button" onClick={this.closeSlideout}>
                            <i className="icon-cancel"/>
                        </div>}
                    {children}
                </div>
            </div>
        );
    }

    private closeSlideout(): void {
        const { handleCloseSlideout, isOpen } = this.props;

        if (isOpen) {
            handleCloseSlideout();
        }
    }
}

export default { SharedSlideout };
